// "Your purchase" — the running summary beside the checkout (stacked below it on
// mobile). Recipient and payer are separate rows on purpose: the person who
// receives the bundle is not necessarily the person whose wallet pays.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { formatMoney } from '@/lib/money';

export interface SummaryModel {
  context: 'personal' | 'company';
  recipientName?: string;
  recipientNumber?: string;
  network?: string;
  product?: string;
  validity?: string;
  price?: number;
  fee?: number;
  currency: string;
  payer?: string;
}

const Row: React.FC<{ k: string; v?: React.ReactNode; strong?: boolean }> = ({ k, v, strong }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className={strong ? 'text-right font-semibold text-foreground tabular-nums' : 'text-right text-foreground tabular-nums break-all'}>
      {v ?? <span className="text-muted-foreground">—</span>}
    </dd>
  </div>
);

export const PurchaseSummary: React.FC<{ model: SummaryModel }> = ({ model }) => {
  const { t } = useTranslation('everydayServices');
  const money = (n: number) => formatMoney(n, model.currency);
  const total = model.price != null ? model.price + (model.fee ?? 0) : undefined;
  return (
    <Card aria-labelledby="es-summary-title">
      <CardHeader className="pb-2">
        <CardTitle id="es-summary-title" className="text-base">{t('summary.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="divide-y divide-border">
          <Row k={t('summary.type')} v={t(`summary.${model.context}`)} />
          <Row k={t('summary.recipient')} v={model.recipientName || model.recipientNumber} />
          {model.recipientName && model.recipientNumber && <Row k={t('summary.number')} v={model.recipientNumber} />}
          <Row k={t('summary.network')} v={model.network} />
          <Row k={t('summary.product')} v={model.product && (model.validity ? `${model.product} · ${model.validity}` : model.product)} />
          <Row k={t('summary.price')} v={model.price != null ? money(model.price) : undefined} />
          <Row k={t('summary.fee')} v={model.price != null ? (model.fee ? money(model.fee) : t('summary.noFee')) : undefined} />
          <Row k={t('summary.total')} v={total != null ? money(total) : undefined} strong />
          <Row k={t('summary.payer')} v={model.payer} />
        </dl>
        {model.context === 'personal' && (
          <p className="flex gap-2 rounded-lg bg-muted p-3 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('summary.privacy')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchaseSummary;
